<template>
  <div
    class="webhook-options-modal"
    data-cy="webhooks-menu"
    :style="style"
    @click.stop.prevent
  >
    <div v-if="isGlobal && dealName" class="px-4 pt-2">({{ dealName }})</div>
    <div
      class="border-b border-gray-100"
      :class="isGlobal ? 'px-4 pb-4 pt-2' : 'p-4'"
    >
      <search-input
        v-model="search"
        no-margin
        no-padding
        :disabled="loading"
        @click.stop="
          (event: Event) => (event.target as HTMLInputElement)?.focus()
        "
        @update:model-value="$emit('search:update', $event)"
      />
    </div>
    <div>
      <loader :is-loading="isGlobal ? triggering || globalLoading : loading" />
      <ul class="max-h-[305px] overflow-auto p-2" @scroll="handleScroll">
        <li
          v-for="webhook in filteredWebhooks"
          :key="webhook.id"
          v-tooltip="{
            content: getTooltipContent(webhook.name),
            placement: 'bottom',
            followCursor: 'vertical'
          }"
          data-cy="webhook-item"
          class="cursor-pointer truncate whitespace-nowrap p-2 text-gray-700 hover:bg-gray-100"
          @mouseenter="checkContentWidth"
        >
          <div class="flex justify-between space-x-2">
            <p
              class="overflow-hidden text-ellipsis whitespace-nowrap"
              data-cy="do-templates-dropdown-option-text"
            >
              {{ webhook.name }}
            </p>
            <button
              class="rounded bg-gradient-to-bl from-primary-button-1 to-primary-button-2 px-2 py-1 text-xs font-medium text-white shadow-primary-button hover:to-primary-button-hover-2 focus:to-primary-button-active active:from-primary-button-active active:to-primary-button-active"
              data-cy="webhook-run"
              @click="
                isGlobal
                  ? emit('webhook:trigger', webhook.id)
                  : triggerCustomWebhook(webhook.id as string)
              "
            >
              {{ $t("COMMON.RUN") }}
            </button>
          </div>
        </li>
        <li
          v-if="!filteredWebhooks.length"
          data-cy="webhooks-no-results"
          class="text-center"
        >
          {{ $t("COMMON.NO_RESULTS") }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { CustomWebhook } from "@/models/customWebhook";
import { usePromiseWrapper } from "@/hooks/common";
import applicationsApiService from "@/services/modules/applications";
import Loader from "@/components/ui/Loader.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";

const emit = defineEmits<{
  "search:update": [string];
  "webhook:trigger": [number | string];
}>();

const {
  items,
  appId,
  style,
  isGlobal,
  triggering = false,
  globalLoading = false
} = defineProps<{
  items: Record<string, string>[] | CustomWebhook[];
  appId: string;
  style: Record<string, string | undefined>;
  isGlobal?: boolean;
  dealName?: string;
  triggering?: boolean;
  globalLoading?: boolean;
}>();

const isTextTruncated = ref(false);

const search = ref("");
const showTooltip = ref(true);
const MAX_NAME_LENGTH = 30;

const filteredWebhooks = computed(() => {
  if (!search.value || isGlobal) {
    return items;
  }
  return items.filter((item) =>
    item.name.toLowerCase().includes(search.value.toLowerCase())
  );
});

const getTooltipContent = (name: string) =>
  name.length > MAX_NAME_LENGTH && showTooltip ? name : "";
const { fetchWrapper: triggerCustomWebhook, loading } = usePromiseWrapper(
  async (whId: string) => {
    if (!appId || !whId) {
      return;
    }
    await applicationsApiService.triggerCustomWebhook(appId, {
      custom_webhook_id: whId
    });
  }
);

const handleScroll = () => {
  // Hide tooltip when scrolling
  showTooltip.value = false;
  showTooltip.value = true;
};

const checkContentWidth = (event: MouseEvent) => {
  const { offsetWidth, scrollWidth } = event.target as HTMLElement;
  isTextTruncated.value = offsetWidth < scrollWidth;
};
</script>

<style scoped>
.webhook-options-modal {
  @apply absolute z-100 max-h-100 min-h-30 min-w-80 max-w-145 rounded bg-white shadow-lg transition-all;
}
</style>
